import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from "@/store/use-auth-store";
import { useGetAllUsers } from "@/hooks/user/use-user";
import { useGetApplicationsForProcess } from "@/hooks/process";
import { ProcessDetailList } from "@/components/process";
import { certificateService } from "@/services/certificate/certificate.service";

const AdminCompletedProcesses: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const { processId } = useParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data: responseData, isLoading, isError } = useGetApplicationsForProcess(
    processId ?? '',
    'completed',
    true,
    undefined,
    { page, limit: 20, search: search || undefined }
  );

  const { data: users = [] } = useGetAllUsers();
  const data = responseData?.data || [];
  const meta = responseData?.meta;

  const { data: certificateTemplate } = useQuery({
    queryKey: ['certificate-template-by-process', processId],
    queryFn: () => certificateService.getTemplateByProcess(processId as string),
    enabled: !!processId,
  });

  const handleSearchChange = useCallback((s: string) => {
    setSearch(s);
    setPage(1);
  }, []);

  if (!processId || !user?.id) {
    return <p className="mt-8 text-center text-primary">{t('processManagement.loading')}</p>;
  }

  return (
    <ProcessDetailList
      data={data}
      isLoading={isLoading}
      isError={isError}
      processId={processId}
      title={t('processManagement.completedApplicationsAdminView')}
      loadingMessage={t('processManagement.loading')}
      errorMessage={t('processManagement.errorLoadingData')}
      noDataMessage={t('processManagement.noCompletedApplicationsFound')}
      baseRoute="/admin/management/completed-applications"
      showEditButton={false}
      showDeleteButton
      showAppliedAt
      showCompletedAt
      showReleaseAll={certificateTemplate?.issuanceType === 'MANUAL'}
      users={users}
      meta={meta}
      onPageChange={setPage}
      search={search}
      onSearchChange={handleSearchChange}
    />
  );
};

export default AdminCompletedProcesses;
